import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Image, Upload } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const galleryCategories = [
  { value: "potong", label: "Model Potong" },
  { value: "warna", label: "Hair Color" },
  { value: "kids", label: "Kids Style" },
];

export default function GalleryManagePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "potong", description: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  const fetchGallery = useCallback(async () => {
    try { const { data } = await api.get("/gallery"); setItems(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/gallery/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Gambar berhasil diupload");
    } catch { toast.error("Upload gagal"); } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gallery", form);
      toast.success("Item galeri ditambahkan");
      setDialogOpen(false);
      fetchGallery();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus item galeri ini?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Item dihapus"); fetchGallery(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  const filteredItems = filterCat === "all" ? items : items.filter(i => i.category === filterCat);

  return (
    <div className="p-6 md:p-8" data-testid="gallery-manage-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Galeri</h1>
          <p className="text-gray-400 text-sm mt-1">Kelola model rambut & hair color</p>
        </div>
        <Button onClick={() => { setForm({ name: "", category: "potong", description: "", image_url: "" }); setDialogOpen(true); }} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10" data-testid="add-gallery-btn">
          <Plus size={16} className="mr-2" /> Tambah
        </Button>
      </div>

      {/* Filter by category */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="gallery-filter-bar">
        {[
          { value: "all", label: `Semua (${items.length})` },
          { value: "potong", label: `Model Potong (${items.filter(i => i.category === "potong").length})` },
          { value: "warna", label: `Hair Color (${items.filter(i => i.category === "warna").length})` },
          { value: "kids", label: `Kids Style (${items.filter(i => i.category === "kids").length})` },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilterCat(opt.value)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${filterCat === opt.value ? "border-[#E60000] bg-[#E60000]/10 text-[#E60000]" : "border-[#262626] text-gray-400 hover:border-[#444]"}`}
            data-testid={`gallery-filter-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-24 bg-[#0F0F0F] border border-[#262626]">
          <Image size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{items.length === 0 ? "Belum ada item galeri" : "Tidak ada item dengan filter ini"}</p>
          {items.length === 0 && <p className="text-gray-500 text-sm mt-1">Tambahkan model rambut dan hair color untuk ditampilkan di website</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gallery-manage-grid">
          {filteredItems.map(item => (
            <div key={item.id} className="bg-[#0F0F0F] border border-[#262626] overflow-hidden group" data-testid={`gallery-manage-item-${item.id}`}>
              <div className="aspect-[3/4] bg-[#1A1A1A] flex items-center justify-center overflow-hidden relative">
                {item.image_url ? (
                  <img src={item.image_url.startsWith("fi-barbershop") ? `${API_BASE}/api/files/${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <Image size={48} className="text-gray-600" />
                )}
                <button onClick={() => handleDelete(item.id)} className="absolute top-2 right-2 w-8 h-8 bg-black/70 flex items-center justify-center text-white hover:text-[#E60000] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-gallery-${item.id}`}><Trash2 size={14} /></button>
              </div>
              <div className="p-3">
                <p className="text-white text-sm font-semibold">{item.name}</p>
                <p className="text-[10px] text-[#E60000] uppercase font-bold">{item.category}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">Tambah Item Galeri</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Upload gambar model rambut atau hair color</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="gallery-form">
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Nama</Label>
              <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="gallery-name-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Kategori</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">{galleryCategories.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Deskripsi</Label>
              <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="gallery-desc-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Upload Gambar</Label>
              <input type="file" accept="image/*" onChange={handleUpload} className="mt-1 w-full text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#262626] file:bg-[#1A1A1A] file:text-white file:text-xs file:font-bold file:uppercase" data-testid="gallery-image-upload" />
              {uploading && <p className="text-xs text-yellow-400 mt-1">Mengupload...</p>}
              {form.image_url && <p className="text-xs text-green-400 mt-1">Gambar terupload</p>}
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Atau URL Gambar</Label>
              <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." data-testid="gallery-url-input" />
            </div>
            <Button type="submit" className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-10 rounded-none" data-testid="gallery-submit-btn">Tambahkan</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
